import { useState } from 'react';
import {
	Button,
	Empty,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

interface FieldClaim {
	id: number;
	imei: string;
	entry_method: 'SCAN' | 'MANUAL';
	state: 'queued' | 'submitted' | 'matched' | 'contested' | 'expired' | 'rejected';
	reject_reason: string | null;
	needs_review: boolean;
	review_reason: string | null;
	agent_id: number;
	agent_name: string;
	device_id: number | null;
	retailer_claimed: number | null;
	contested_with: number | null;
	flags: Record<string, unknown>;
	claimed_at: string;
}

const REJECT_REASONS = [
	{ value: 'NOT_FOUND', label: 'Not found' },
	{ value: 'ALREADY_CLAIMED', label: 'Already claimed' },
	{ value: 'OUTSIDE_WINDOW', label: 'Outside window' },
	{ value: 'CONTRACT_CANCELLED', label: 'Contract cancelled' },
];

// Why a claim landed in the queue, in words an admin can act on.
const REVIEW_REASON_LABELS: Record<string, string> = {
	clock_drift: "The handset's clock was wrong, so the claim window could not be measured",
	outside_window: 'Claimed more than 24 hours from the sale',
	outside_outer_bound: 'Arrived more than 96 hours after the sale',
};

export default function FieldClaims() {
	const queryClient = useQueryClient();

	const [rejecting, setRejecting] = useState<FieldClaim | null>(null);
	const [rejectReason, setRejectReason] = useState<string | undefined>();

	const { data: claims = [], isLoading, error } = useQuery<FieldClaim[]>({
		queryKey: ['field-claims-review'],
		queryFn: async () => {
			const res = await axios.get('/admin/field/claims/review', { withCredentials: true });
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const resolve = useMutation({
		mutationFn: async (payload: {
			id: number;
			action: 'award' | 'reject';
			reject_reason?: string;
		}) => {
			await axios.post(
				`/admin/field/claims/${payload.id}/resolve`,
				{ action: payload.action, reject_reason: payload.reject_reason },
				{ withCredentials: true },
			);
		},
		onSuccess: (_data, variables) => {
			message.success(
				variables.action === 'award'
					? 'Claim awarded. Any competing claim has been rejected.'
					: 'Claim rejected',
			);
			queryClient.invalidateQueries({ queryKey: ['field-claims-review'] });
			setRejecting(null);
			setRejectReason(undefined);
		},
		onError: handleAPIError,
	});

	const confirmAward = (claim: FieldClaim) => {
		const contested = claim.state === 'contested';
		Modal.confirm({
			title: `Award ${claim.imei} to ${claim.agent_name}?`,
			width: 520,
			content: (
				<Space direction="vertical" size={8} style={{ marginTop: 8 }}>
					<span>This attributes the sale to {claim.agent_name} for commission.</span>
					{contested && (
<span>Awarding this rejects the competing claim.</span>
					)}
				</Space>
			),
			okText: 'Award claim',
			onOk: () => resolve.mutateAsync({ id: claim.id, action: 'award' }),
		});
	};

	const columns = [
		{
			title: 'IMEI',
			key: 'imei',
			render: (_: unknown, r: FieldClaim) => (
				<Space direction="vertical" size={0}>
					<span style={{ fontFamily: 'monospace' }}>{r.imei}</span>
					<Space size={4}>
						<Tag color={r.entry_method === 'SCAN' ? 'blue' : 'orange'}>
							{r.entry_method}
						</Tag>
						{r.flags?.retailer_mismatch ? (
							<Tooltip title="Retailer differs from contract">
								<Tag color="gold">Shop mismatch</Tag>
							</Tooltip>
						) : null}
					</Space>
				</Space>
			),
		},
		{
			title: 'Agent',
			key: 'agent',
			render: (_: unknown, r: FieldClaim) => (
				<Space direction="vertical" size={0}>
					<span>{r.agent_name}</span>
					<span style={{ fontSize: 12, color: '#888' }}>
						{dayjs(r.claimed_at).format('DD MMM YYYY, HH:mm')}
					</span>
				</Space>
			),
		},
		{
			title: 'Why it is here',
			key: 'reason',
			render: (_: unknown, r: FieldClaim) =>
				r.state === 'contested' ? (
					<Space direction="vertical" size={0}>
						<Tag color="red">Contested</Tag>
						<span style={{ fontSize: 12, color: '#888' }}>
							Two agents claimed this sale
						</span>
					</Space>
				) : (
					<Space direction="vertical" size={0}>
						<Tag color="orange">Held for review</Tag>
						<span style={{ fontSize: 12, color: '#888' }}>
							{REVIEW_REASON_LABELS[r.review_reason ?? ''] ?? r.review_reason}
						</span>
					</Space>
				),
		},
		{
			title: 'Contract',
			key: 'device',
			render: (_: unknown, r: FieldClaim) =>
				r.device_id ? (
					<a href={`/admin/devices?device=${r.device_id}`}>#{r.device_id}</a>
				) : (
					<Tooltip title="No contract matched this IMEI">
						<span style={{ color: '#999' }}>None</span>
					</Tooltip>
				),
		},
		{
			title: 'Action',
			key: 'action',
			render: (_: unknown, r: FieldClaim) => (
				<Space>
					<Tooltip
						title={!r.device_id ? 'No matched contract' : ''}
					>
						<Button
							size="small"
							type="primary"
							disabled={!r.device_id}
							onClick={() => confirmAward(r)}
						>
							Award
						</Button>
					</Tooltip>
					<Button size="small" danger onClick={() => setRejecting(r)}>
						Reject
					</Button>
				</Space>
			),
		},
	];

	return (
		<AppLayout
			title="Field Claims Review"
			subtitle="Sale claims that could not be resolved automatically"
		>
			<div style={{ padding: 16 }}>
				<Space direction="vertical" size={12} style={{ width: '100%' }}>
					<Table
						rowKey="id"
						loading={isLoading}
						dataSource={claims}
						columns={columns}
						pagination={{ pageSize: 20 }}
						locale={{ emptyText: <Empty description="Nothing waiting for review" /> }}
					/>
				</Space>
			</div>

			<Modal
				open={!!rejecting}
				title={`Reject claim ${rejecting?.imei}?`}
				okText="Reject claim"
				okButtonProps={{ danger: true, disabled: !rejectReason }}
				confirmLoading={resolve.isPending}
				onCancel={() => {
					setRejecting(null);
					setRejectReason(undefined);
				}}
				onOk={() =>
					rejecting &&
					resolve.mutate({
						id: rejecting.id,
						action: 'reject',
						reject_reason: rejectReason,
					})
				}
			>
				<Space direction="vertical" size={8} style={{ width: '100%' }}>
					<span>
						{rejecting?.agent_name} will see this reason in the app, so pick the one that
						actually applies.
					</span>
					<Select
						style={{ width: '100%' }}
						placeholder="Reason"
						value={rejectReason}
						onChange={setRejectReason}
						options={REJECT_REASONS}
					/>
				</Space>
			</Modal>
		</AppLayout>
	);
}
